import React, { useState, useEffect } from "react";
import { Add, AutoAwesome, ImportExport, MoreVert } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { Layout } from "../Layouts/Layout";
import { TeamList } from "../Components/Team/List";
import { MatchingRun, MatchingRunStatus, Team as TeamType } from "../types";
import { SectionSelector } from "../Components/Section/Selector";
import {
  GET_SECTIONS,
  SectionsResult,
  GET_GROUPS,
  GroupsResult,
  UPDATE_GROUP,
  SYNC_COURSE,
  CourseResult,
  GET_COURSE,
  SET_COURSE_MODE,
  GET_ALL_AND_SECTION_USERS,
  TeamsUsersResult,
  SET_INCLUDE_INSTRUCTORS_IN_TEAMS,
  GET_MATCHING_RUNS,
  TRIGGER_MATCHING,
  DELETE_MATCHING_RUN,
  MatchingRunsResult,
} from "../Queries";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { ImportModal } from "../Components/Team/ImportModal";
import { LoadingButton } from "@mui/lab";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { TeamRoster } from "../Components/Team/Roster";
import { TeamNew } from "../Components/Team/New";
import { MatchingResultCard } from "../Components/Matching/MatchingResultCard";
import { MatchingWizard } from "../Components/Matching/MatchingWizard";
import { MatchingPreviewModal } from "../Components/Matching/MatchingPreviewModal";
import { useFeatures } from "../Contexts/FeaturesContext";
import { useResizeDetector } from "react-resize-detector";

// TODO: Potentially extract this to a separate component and used on similar headers
//
// Widths below are the point at which the labels start colliding, with the
// narrower pair applying when the Match action is hidden by feature flag.
// Browser-measured (FL-309 review): with Match visible the full tier fits
// down to a 693px toolbar, short to 538px, and the overflow row to 397px —
// so every tier switches before its row wraps.
const TOOLBAR_OVERFLOW_BELOW = { withMatch: 600, withoutMatch: 500 };
const TOOLBAR_SHORT_BELOW = { withMatch: 760, withoutMatch: 620 };

type ToolbarMode = "full" | "short" | "overflow";

export const toolbarModeForWidth = (
  width: number | undefined,
  matchingEnabled: boolean,
): ToolbarMode => {
  // Undefined on first paint, before the ResizeObserver has reported. Default
  // to the roomiest tier so labels never flash from short to full.
  if (width === undefined) return "full";
  const key = matchingEnabled ? "withMatch" : "withoutMatch";
  if (width < TOOLBAR_OVERFLOW_BELOW[key]) return "overflow";
  if (width < TOOLBAR_SHORT_BELOW[key]) return "short";
  return "full";
};

export const Teams = () => {
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();
  const { hasFeature } = useFeatures();
  const matchingEnabled = hasFeature("team-matching");

  //
  // State
  //

  const [importOpen, setImportOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [sectionId, setSectionId] = useState(undefined as string | undefined);
  const [selected, setSelected] = useState<string[]>([]);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [matchingWizardOpen, setMatchingWizardOpen] = useState(false);
  const [matchingPreviewOpen, setMatchingPreviewOpen] = useState(false);
  const [applyToastOpen, setApplyToastOpen] = useState(false);
  const [applyToastMsg, setApplyToastMsg] = useState("");
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null);
  const actionMenuOpen = Boolean(actionAnchor);

  //
  // Responsive toolbar
  //

  const { width: toolbarWidth, ref: toolbarRef } =
    useResizeDetector<HTMLDivElement>({
      handleHeight: false,
      // Unthrottled, every ResizeObserver tick re-renders the whole Teams
      // tree (roster + team list) during a window drag or drawer animation.
      refreshMode: "throttle",
      refreshRate: 150,
    });
  const toolbarMode = toolbarModeForWidth(toolbarWidth, matchingEnabled);

  // If a resize removes the overflow trigger while its menu is open, the
  // anchor now points at a detached node; narrowing again would remount the
  // menu open but anchored nowhere (MUI "anchorEl is invalid", menu pinned
  // to the viewport corner). Drop the anchor whenever the trigger leaves.
  useEffect(() => {
    if (toolbarMode !== "overflow") setActionAnchor(null);
  }, [toolbarMode]);

  //
  // Queries
  //

  const { loading: sectionsLoading, data: sectionsData } =
    useQuery<SectionsResult>(GET_SECTIONS, {
      variables: { filters: {} },
      onError: (error) => setError(t("errors.sections.list"), error),
    });

  const {
    loading: courseLoading,
    data: courseData,
    refetch: refetchCourse,
  } = useQuery<CourseResult>(GET_COURSE, {
    onError: (error) => setError(t("errors.courses.get"), error),
  });

  const {
    loading: groupsLoading,
    data: groupsData,
    refetch: refetchGroups,
  } = useQuery<GroupsResult>(GET_GROUPS, {
    variables: {
      filters: {
        // section: sectionId ? sectionId : undefined,
      },
    },
    onError: (error) => setError(t("errors.groups.list"), error),
  });

  const teams = groupsData ? groupsData.groups : undefined;

  const course = courseData ? courseData.course : undefined;

  const {
    data: matchingRunsData,
    refetch: refetchMatchingRuns,
    startPolling,
    stopPolling,
  } = useQuery<MatchingRunsResult>(GET_MATCHING_RUNS);

  // Only DR/SA/PR/RD are "live" — Applied/LmsSynced are terminal.
  const LIVE_STATUSES = new Set<MatchingRunStatus>([
    MatchingRunStatus.Draft,
    MatchingRunStatus.SurveyActive,
    MatchingRunStatus.Processing,
    MatchingRunStatus.Ready,
  ]);

  const activeMatchingRun: MatchingRun | undefined =
    matchingRunsData?.matchingRuns?.find((r) => LIVE_STATUSES.has(r.status));

  // Dynamic polling: fast refresh while Processing, slower otherwise, stop when idle.
  useEffect(() => {
    const status = activeMatchingRun?.status;
    if (status === MatchingRunStatus.Processing) {
      // Poll quickly while the engine is running (typically 30–120 s in prod)
      startPolling(5_000);
    } else if (status === MatchingRunStatus.SurveyActive) {
      // Moderate refresh — survey response count matters but changes slowly
      startPolling(15_000);
    } else if (
      status === MatchingRunStatus.Draft ||
      status === MatchingRunStatus.Ready
    ) {
      // Low-frequency background refresh — run is not time-sensitive
      startPolling(30_000);
    } else {
      // No active run (or terminal status) — stop polling
      stopPolling();
    }
    return () => stopPolling();
  }, [activeMatchingRun?.status, startPolling, stopPolling]);

  // Only show the card for active (live) runs — once applied/synced, hide the card.
  // After apply the toast informs the instructor; the card disappears.
  const displayMatchingRun: MatchingRun | undefined = activeMatchingRun;

  const [triggerMatching, { loading: triggerLoading }] = useMutation(
    TRIGGER_MATCHING,
    {
      onCompleted: () => refetchMatchingRuns(),
      onError: (error) => setError("Failed to trigger matching", error),
    },
  );

  const [deleteMatchingRun] = useMutation(DELETE_MATCHING_RUN, {
    onCompleted: () => refetchMatchingRuns(),
    onError: (error) => setError("Failed to cancel matching run", error),
  });

  const { loading: usersLoading, data: usersData } = useQuery<TeamsUsersResult>(
    GET_ALL_AND_SECTION_USERS,
    {
      variables: {
        filters: {
          sections: sectionId ? sectionId : undefined,
          // unassigned: true
        },
      },
      onError: (error) => setError(t("errors.users.list"), error),
    },
  );

  //
  // Mutations
  //

  const [updateGroup] = useMutation(UPDATE_GROUP, {
    //refetchQueries: ["GetGroups"],
    onError: (error) => setError(t("errors.groups.update"), error),
  });

  const [syncCourse] = useMutation(SYNC_COURSE, {
    refetchQueries: ["GetGroups", "GetSections"],
    onError: (error) => setError(t("errors.courses.sync"), error),
  });
  const [setCourseMode] = useMutation(SET_COURSE_MODE, {
    // refetchQueries: [""]
    onError: (error) => setError(t("errors.courses.setMode"), error),
  });
  const [setIncludeInstructorsInTeams] = useMutation(
    SET_INCLUDE_INSTRUCTORS_IN_TEAMS,
    {
      refetchQueries: ["GetCourse"],
      onError: (error) =>
        setError(t("errors.courses.setIncludeInstructors"), error),
    },
  );

  const sections = sectionsData ? sectionsData.sections : undefined;
  const section = sections
    ? sections.find((target) => target.id === sectionId)
    : undefined;

  if (!sectionsLoading && !sections) {
    throw new Error("No sections found");
  }

  // Determine if instructors should be included based on course preference
  const includeInstructors = course?.includeInstructorsInTeams ?? false;

  // Filter users based on includeInstructors preference
  const sectionUsers = includeInstructors
    ? usersData?.sectionUsers.filter(
        (user) => user.isLearner || user.isInstructor,
      )
    : usersData?.sectionUsers.filter((user) => user.isLearner);
  const allUsers = includeInstructors
    ? usersData?.allUsers.filter((user) => user.isLearner || user.isInstructor)
    : usersData?.allUsers.filter((user) => user.isLearner);

  //
  // Event Handlers
  //

  function handleTeamChange(team: TeamType) {
    updateGroup({
      variables: team,
      context: {
        debounceKey: `team.${team.id}`,
      },
      optimisticResponse: {
        updateGroup: {
          id: team.id,
          __typename: "Group",
          name: team.name,
          userIds: team.userIds,
        },
      },
    });
  }

  function handleImportTeams() {
    if (course?.fullSyncEnabled && course?.fullSyncReady) {
      importTeams();
    } else {
      setImportOpen(true);
    }
  }

  function handleImportClose() {
    setImportOpen(false);
  }

  function handleImportConnected() {
    refetchCourse();
  }

  async function handleImportEnable(enabled: boolean) {
    await setCourseMode({
      variables: {
        fullSync: enabled,
      },
    });

    setImportOpen(false);
    importTeams();
  }

  function handleAssignment(userIds: string[], team: TeamType) {
    handleTeamChange({ ...team, userIds: [...team.userIds, ...userIds] });
    setSelected([]);
  }

  async function handleIncludeInstructorsChange(include: boolean) {
    await setIncludeInstructorsInTeams({
      variables: {
        includeInstructors: include,
      },
    });
  }

  //
  // Service
  //

  async function importTeams() {
    setIsImporting(true);
    await syncCourse();
    setIsImporting(false);
  }

  const showLoading = sectionsLoading || groupsLoading || usersLoading;

  return (
    <Layout
      breadcrumbs={[
        { title: t("breadcrumbs.teams") },
        { title: t("breadcrumbs.teamList") },
      ]}
      loading={showLoading}
    >
      {importOpen && course && (
        <ImportModal
          course={course}
          onClose={handleImportClose}
          onConnect={handleImportConnected}
          onEnable={handleImportEnable}
        />
      )}
      {matchingEnabled && course?.id && (
        <MatchingWizard
          open={matchingWizardOpen}
          courseId={course.id}
          existingRun={activeMatchingRun}
          onClose={() => setMatchingWizardOpen(false)}
          onLaunched={() => {
            setMatchingWizardOpen(false);
            refetchMatchingRuns();
          }}
        />
      )}
      {matchingEnabled && displayMatchingRun && (
        <MatchingPreviewModal
          open={matchingPreviewOpen}
          run={displayMatchingRun}
          onClose={() => setMatchingPreviewOpen(false)}
          onApplied={(updated) => {
            setMatchingPreviewOpen(false);
            refetchMatchingRuns();
            // Refetch groups so the Teams view reflects the new teams immediately
            refetchGroups();
            setApplyToastMsg(
              updated.status === MatchingRunStatus.LmsSyncing
                ? "Teams have been applied and are being synced to Canvas."
                : "Teams have been applied to the course roster.",
            );
            setApplyToastOpen(true);
          }}
        />
      )}
      <Box
        ref={toolbarRef}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        {!sectionsLoading && sections ? (
          <SectionSelector
            sections={sections}
            selected={section}
            onChange={(section) => setSectionId(section ? section.id : "")}
          />
        ) : (
          <>Sections loading...</>
        )}
        <Typography sx={{ ml: 2 }}>
          {(teams ?? []).length} Team{teams?.length === 1 ? "" : "s"}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {toolbarMode !== "overflow" && (
            <Button
              variant="text"
              aria-label="start new team"
              endIcon={<Add />}
              onClick={() => setShowForm(true)}
              sx={{ color: "white" }}
            >
              {toolbarMode === "short" ? "New" : "New Team"}
            </Button>
          )}
          {matchingEnabled && toolbarMode !== "overflow" && (
            <Button
              variant="outlined"
              aria-label="match teams"
              endIcon={<AutoAwesome />}
              onClick={() => setMatchingWizardOpen(true)}
              sx={{ color: "white", borderColor: "white" }}
            >
              {toolbarMode === "short" ? "Match" : "Match Teams"}
            </Button>
          )}
          <Tooltip
            title={
              courseLoading || course === undefined
                ? ""
                : !course.fullSyncCapable
                  ? "Your LMS isn't configured for full API access; please contact an administrator to enable full sync"
                  : "Import teams and sections from your LMS"
            }
          >
            <span>
              <LoadingButton
                disabled={
                  courseLoading ||
                  course === undefined ||
                  !course.fullSyncCapable
                }
                variant="contained"
                aria-label="import teams"
                endIcon={<ImportExport />}
                onClick={handleImportTeams}
                loading={isImporting}
              >
                {isImporting
                  ? "Importing..."
                  : toolbarMode === "full"
                    ? "Import Teams"
                    : "Import"}
              </LoadingButton>
            </span>
          </Tooltip>
          {toolbarMode === "overflow" && (
            <>
              <IconButton
                aria-label="more team actions"
                aria-controls={actionMenuOpen ? "teams-action-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={actionMenuOpen ? "true" : undefined}
                onClick={(e) => setActionAnchor(e.currentTarget)}
                sx={{ minWidth: 48, minHeight: 48 }}
              >
                <MoreVert />
              </IconButton>
              <Menu
                id="teams-action-menu"
                anchorEl={actionAnchor}
                open={actionMenuOpen}
                onClose={() => setActionAnchor(null)}
                onClick={() => setActionAnchor(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem
                  aria-label="start new team"
                  onClick={() => setShowForm(true)}
                >
                  <ListItemIcon>
                    <Add fontSize="small" />
                  </ListItemIcon>
                  New Team
                </MenuItem>
                {matchingEnabled && (
                  <MenuItem
                    aria-label="match teams"
                    onClick={() => setMatchingWizardOpen(true)}
                  >
                    <ListItemIcon>
                      <AutoAwesome fontSize="small" />
                    </ListItemIcon>
                    Match Teams
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      </Box>
      {matchingEnabled && displayMatchingRun && (
        <MatchingResultCard
          run={displayMatchingRun}
          onOpenPreview={() => setMatchingPreviewOpen(true)}
          onTriggerMatching={() =>
            activeMatchingRun &&
            triggerMatching({ variables: { runId: activeMatchingRun.id } })
          }
          triggerLoading={triggerLoading}
          onOpenWizard={() => setMatchingWizardOpen(true)}
          onCancelRun={
            activeMatchingRun
              ? () =>
                  deleteMatchingRun({
                    variables: { runId: activeMatchingRun.id },
                  })
              : undefined
          }
        />
      )}
      <Grid container spacing={2}>
        <TeamRoster
          teams={teams}
          users={sectionUsers}
          selectedUsers={selected}
          isLoading={usersLoading}
          includeInstructors={includeInstructors}
          onIncludeInstructorsChange={handleIncludeInstructorsChange}
          onAssign={handleAssignment}
          onSelect={setSelected}
        />
        <Grid item xs={12} md={6} lg={6}>
          {showForm && (
            <TeamNew
              onClose={() => setShowForm(false)}
              users={sectionUsers || []}
            />
          )}
          <TeamList
            teams={teams}
            sectionStudents={sectionUsers}
            allStudents={allUsers}
            selectedSection={section}
            isCreating={showForm}
            isLoading={groupsLoading}
            onNew={() => setShowForm(true)}
            onAssign={handleAssignment}
            onChange={handleTeamChange}
          />
        </Grid>
      </Grid>
      <Snackbar
        open={applyToastOpen}
        autoHideDuration={4000}
        onClose={() => setApplyToastOpen(false)}
        message={applyToastMsg}
      />
    </Layout>
  );
};
